import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as auditLogApi from '../../api/auditLogApi';
import { useSnackbar } from '../../context/SnackbarContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ACTION_COLORS = { CREATE: 'success', UPDATE: 'info', DELETE: 'error' };

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);

  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    auditLogApi.getEntities().then((res) => setEntities(res.data || res)).catch(console.error);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, pageSize };
      if (filterEntity) params.entity = filterEntity;
      if (filterAction) params.action = filterAction;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const res = await auditLogApi.getAll(params);
      const data = res.data || res;
      setLogs(data.logs || []); setTotal(data.total || 0);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [page, pageSize, filterEntity, filterAction, filterFrom, filterTo, showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setFilterEntity(''); setFilterAction(''); setFilterFrom(''); setFilterTo(''); setPage(0); };
  const hasFilters = filterEntity || filterAction || filterFrom || filterTo;

  const formatValue = (val) => {
    if (!val) return '—';
    try { return JSON.stringify(val, null, 2); } catch { return String(val); }
  };

  return (
    <Box>
      <PageHeader title="Audit Logs" subtitle="Complete trail of all create, update, and delete operations in the system." />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterListIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
            {hasFilters && <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: 'auto' }}>Clear All</Button>}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity</InputLabel>
                <Select value={filterEntity} label="Entity" onChange={(e) => { setFilterEntity(e.target.value); setPage(0); }}>
                  <MenuItem value="">All</MenuItem>
                  {entities.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select value={filterAction} label="Action" onChange={(e) => { setFilterAction(e.target.value); setPage(0); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="CREATE">CREATE</MenuItem>
                  <MenuItem value="UPDATE">UPDATE</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField size="small" fullWidth label="From Date" type="date" InputLabelProps={{ shrink: true }}
                value={filterFrom} onChange={(e) => { setFilterFrom(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField size="small" fullWidth label="To Date" type="date" InputLabelProps={{ shrink: true }}
                value={filterTo} onChange={(e) => { setFilterTo(e.target.value); setPage(0); }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        {loading ? <LoadingSpinner /> : (
          <>
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Entity Name / ID</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover sx={{ '& > td': { borderBottom: expandedRow === log.id ? 'none' : undefined } }}>
                      <TableCell>
                        {(log.oldValue || log.newValue) && (
                          <IconButton size="small" onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
                            {expandedRow === log.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{new Date(log.createdAt).toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{log.user?.username || <span style={{ color: '#9e9e9e' }}>System</span>}</Typography>
                      </TableCell>
                      <TableCell><Chip label={log.action} size="small" color={ACTION_COLORS[log.action] || 'default'} /></TableCell>
                      <TableCell><Chip label={log.entity} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.entityName || '—'}</Typography>
                        {log.entityId && <Typography variant="caption" color="text.disabled">ID: {log.entityId}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#9e9e9e' }}>{log.ipAddress || '—'}</TableCell>
                    </TableRow>
                    {(log.oldValue || log.newValue) && (
                      <TableRow key={`${log.id}-detail`}>
                        <TableCell colSpan={7} sx={{ p: 0 }}>
                          <Collapse in={expandedRow === log.id} timeout="auto">
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 2, bgcolor: '#FAFAFA' }}>
                              {log.oldValue && (
                                <Box>
                                  <Typography variant="caption" color="error.main" fontWeight={700}>BEFORE</Typography>
                                  <Box component="pre" sx={{ fontSize: 11, mt: 0.5, p: 1, bgcolor: '#FFF3F3', borderRadius: 1, overflow: 'auto', maxHeight: 150 }}>
                                    {formatValue(log.oldValue)}
                                  </Box>
                                </Box>
                              )}
                              {log.newValue && (
                                <Box>
                                  <Typography variant="caption" color="success.main" fontWeight={700}>AFTER</Typography>
                                  <Box component="pre" sx={{ fontSize: 11, mt: 0.5, p: 1, bgcolor: '#F3FFF3', borderRadius: 1, overflow: 'auto', maxHeight: 150 }}>
                                    {formatValue(log.newValue)}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {logs.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No audit logs found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[50]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
